import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { lightImpactVibrate } from '../../../../utils/appBridge'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  TaxHarvestingButtonLabel,
  trackTaxHarvestingScreenLoaded,
  trackTaxHarvestingButtonTap,
  trackTaxHarvestingUserClicked
} from '../../tracking/taxHarvestingTracking'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { fetchWithdrawals } from '../../api/withdrawalsApi'
import { fetchTaxHarvestingSummary, getSelectedTaxHarvestingFinancialYear } from '../../api/taxHarvestingSummary'
import { WithdrawalsResponse, HarvestTypeFilter } from '../../types/withdrawals.types'
import { transactionMatchesFilter } from '../../utils/withdrawalsUtils'
import { WithdrawalsPageHeader } from '../../components/withdrawals/WithdrawalsPageHeader'
import { WithdrawalsSummaryCard } from '../../components/withdrawals/WithdrawalsSummaryCard'
import { WithdrawalsFilterChips } from '../../components/withdrawals/WithdrawalsFilterChips'
import { WithdrawalsTransactionsSection } from '../../components/withdrawals/WithdrawalsTransactionsSection'
import LtcgBottomSheet from '../../bottom-sheets/LtcgBottomSheet'
import StcgBottomSheet from '../../bottom-sheets/StcgBottomSheet'
import ThreeLineLoading from '../../../../components/ui-components/loading/ThreeLineLoading'
import { formatFyDisplay } from '../../utils/misc'

export default function WithdrawalsPage() {
  const navigate = useNavigate()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const selectedFinancialYear = getSelectedTaxHarvestingFinancialYear()
  const { data, isLoading, isError } = useQuery<WithdrawalsResponse>({
    queryKey: ['taxHarvestingWithdrawals', selectedFinancialYear],
    queryFn: () => fetchWithdrawals(selectedFinancialYear),
  })
  const { data: summaryData } = useQuery({
    queryKey: ['taxHarvestingSummary', selectedFinancialYear],
    queryFn: () => fetchTaxHarvestingSummary(selectedFinancialYear),
  })

  const [selectedFilters, setSelectedFilters] = useState<HarvestTypeFilter[]>([HarvestTypeFilter.ALL])
  const scrollRef = useRef<HTMLDivElement>(null)
  const aboveChipsRef = useRef<HTMLDivElement>(null)
  const chipsBarRef = useRef<HTMLDivElement>(null)
  const [aboveChipsHeight, setAboveChipsHeight] = useState(0)
  const [chipsBarHeight, setChipsBarHeight] = useState(0)

  useLayoutEffect(() => {
    const el = aboveChipsRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setAboveChipsHeight(el.offsetHeight))
    ro.observe(el)
    setAboveChipsHeight(el.offsetHeight)
    return () => ro.disconnect()
  }, [])

  useLayoutEffect(() => {
    const el = chipsBarRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setChipsBarHeight(el.offsetHeight))
    ro.observe(el)
    setChipsBarHeight(el.offsetHeight)
    return () => ro.disconnect()
  }, [])

  const totalWithdrawal = data?.totalWithdrawal ?? 0
  const totalRealisedGains = data?.totalRealisedGains ?? 0
  const netRealisedLtcg = data?.netRealisedLtcg ?? 0
  const netRealisedStcg = data?.netRealisedStcg ?? 0
  const list = data?.withdrawTransactionList ?? []
  const hasWithdrawals = list.length > 0
  const paddingStart = hasWithdrawals ? aboveChipsHeight + chipsBarHeight : aboveChipsHeight

  const activeFilters = selectedFilters

  const filteredList = useMemo(
    () => list.filter((txn) => transactionMatchesFilter(txn, activeFilters)),
    [list, activeFilters]
  )

  const fyWithdrawalsLabel = useMemo(
    () =>
      formatFyDisplay({
        format: 'range',
        currentFyStart: summaryData?.currentFyStart,
        currentFyEnd: summaryData?.currentFyEnd,
        currentFy: summaryData?.currentFy
      }),
    [summaryData?.currentFyStart, summaryData?.currentFyEnd, summaryData?.currentFy]
  )

  const isEmptyList = list.length === 0
  const isEmptyFiltered = !isEmptyList && filteredList.length === 0

  const toggleFilter = (value: HarvestTypeFilter) => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Withdrawals,
      eventId: TaxHarvestingEventId.FilterChip,
      buttonLabel: value
    })
    setSelectedFilters([value])
  }

  const handleBack = () => {
    trackTaxHarvestingButtonTap({
      screen: TaxHarvestingScreenName.Withdrawals,
      eventId: TaxHarvestingEventId.Back,
      buttonLabel: TaxHarvestingButtonLabel.Back
    })
    lightImpactVibrate?.()
    navigate(-1)
  }

  useEffect(() => {
    trackTaxHarvestingScreenLoaded(TaxHarvestingScreenName.Withdrawals)
  }, [])

  const openLtcgSheet = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Withdrawals,
      eventId: TaxHarvestingEventId.NetRealisedLtcg
    })
    openBottomSheet(
      <LtcgBottomSheet
        view="summary"
        summary={summaryData}
        onDismiss={closeBottomSheet}
      />
    )
  }

  const openStcgSheet = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Withdrawals,
      eventId: TaxHarvestingEventId.NetRealisedStcg
    })
    openBottomSheet(
      <StcgBottomSheet
        view="summary"
        summary={summaryData}
        onDismiss={closeBottomSheet}
      />
    )
  }

  if (isLoading) {
    return (
      <ThreeLineLoading />
    )
  }

  if (isError) {
    return (
      <div className="h-screen bg-[#F5F5F5] flex flex-col overflow-hidden">
        <WithdrawalsPageHeader onBack={handleBack} />
        <main className="flex-1 flex items-center justify-center px-5 min-h-0">
          <p className="text-[14px] font-[400] text-[#6A6E6E]">
            Something went wrong. Please try again.
          </p>
        </main>
      </div>
    )
  }

  return (
    <div className="h-screen bg-white flex flex-col overflow-x-hidden">
      <WithdrawalsPageHeader onBack={handleBack} />

      <main
        ref={scrollRef}
        className="flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden pb-4 bg-white"
      >
        <div ref={aboveChipsRef} className="w-full">
          <div className="px-5 w-full pt-2">
            <WithdrawalsSummaryCard
              totalWithdrawal={totalWithdrawal}
              totalRealisedGains={totalRealisedGains}
              netRealisedLtcg={netRealisedLtcg}
              netRealisedStcg={netRealisedStcg}
              onNetRealisedLtcgClick={openLtcgSheet}
              onNetRealisedStcgClick={openStcgSheet}
            />
          </div>
          <div className="px-5 pb-4 w-full">
            <h2 className="shrink-0 text-[16px] font-[500] text-[#232323]">Withdrawals</h2>
            {fyWithdrawalsLabel ? (
              <p className="shrink-0 text-[13px] font-[400] text-[#6A6E6E] pt-1">
                {fyWithdrawalsLabel}
              </p>
            ) : null}
          </div>
        </div>

        {hasWithdrawals && (
          <div
            ref={chipsBarRef}
            className="sticky top-0 z-10 bg-white px-5 pb-1"
          >
            <WithdrawalsFilterChips
              activeFilters={activeFilters}
              onToggleFilter={toggleFilter}
            />
          </div>
        )}

        <WithdrawalsTransactionsSection
          scrollElementRef={paddingStart > 0 ? scrollRef : undefined}
          paddingStart={paddingStart}
          activeFilters={activeFilters}
          filteredList={filteredList}
          isEmptyList={isEmptyList}
          isEmptyFiltered={isEmptyFiltered}
        />
      </main>
    </div>
  )
}
